/**
 * Keyboard Shortcuts Types
 * Type definitions for global keyboard shortcuts system
 */

export interface KeyboardShortcut {
  id: string;
  key: string; // The key combination (e.g., 'mod+n', 'mod+k')
  description: string; // Human-readable description
  category: 'navigation' | 'action' | 'editor' | 'general';
  action: () => void | Promise<void>;
  enabled?: boolean; // Whether the shortcut is currently enabled
  requiresAuth?: boolean; // Whether the shortcut requires authentication
  context?: 'global' | 'chat' | 'editor'; // Where the shortcut is active
}

export interface KeyboardShortcutCategory {
  id: string;
  label: string;
  shortcuts: KeyboardShortcut[];
}

export interface KeyboardShortcutsContextValue {
  shortcuts: KeyboardShortcut[];
  registerShortcut: (shortcut: KeyboardShortcut) => void;
  unregisterShortcut: (id: string) => void;
  isShortcutEnabled: (id: string) => boolean;
  enableShortcut: (id: string) => void;
  disableShortcut: (id: string) => void;
  openHelp: () => void;
  closeHelp: () => void;
  isHelpOpen: boolean;
}

/**
 * Platform detection utilities
 */
export const isMac = (): boolean => {
  if (typeof window === 'undefined') return false;
  return navigator.platform.toUpperCase().indexOf('MAC') >= 0;
};

export const isWindows = (): boolean => {
  if (typeof window === 'undefined') return false;
  return navigator.platform.toUpperCase().indexOf('WIN') >= 0;
};

/**
 * Format keyboard shortcut for display
 * Converts 'mod+n' to '⌘N' on Mac or 'Ctrl+N' on Windows
 */
export const formatShortcut = (shortcut: string): string => {
  const mac = isMac();

  return shortcut
    .split('+')
    .map((key) => {
      switch (key.toLowerCase()) {
        case 'mod':
          return mac ? '⌘' : 'Ctrl';
        case 'ctrl':
          return mac ? '⌃' : 'Ctrl';
        case 'alt':
          return mac ? '⌥' : 'Alt';
        case 'shift':
          return mac ? '⇧' : 'Shift';
        case 'enter':
          return mac ? '↵' : 'Enter';
        case 'escape':
        case 'esc':
          return 'Esc';
        case 'arrowup':
          return '↑';
        case 'arrowdown':
          return '↓';
        case 'arrowleft':
          return '←';
        case 'arrowright':
          return '→';
        default:
          return key.toUpperCase();
      }
    })
    .join(mac ? '' : '+');
};

/**
 * Check if a keyboard event matches a shortcut pattern
 */
export const matchesShortcut = (event: KeyboardEvent, pattern: string): boolean => {
  const parts = pattern.toLowerCase().split('+');
  const key = parts[parts.length - 1];
  const modifiers = parts.slice(0, -1);

  // Check key match
  const keyMatch = event.key.toLowerCase() === key.toLowerCase();
  if (!keyMatch) return false;

  // Check modifiers
  const hasMod = modifiers.includes('mod');
  const hasCtrl = modifiers.includes('ctrl');
  const hasAlt = modifiers.includes('alt');
  const hasShift = modifiers.includes('shift');

  const modMatch = hasMod ? (event.metaKey || event.ctrlKey) : true;
  const ctrlMatch = hasCtrl ? event.ctrlKey : !event.ctrlKey || hasMod;
  const altMatch = hasAlt ? event.altKey : !event.altKey;
  const shiftMatch = hasShift ? event.shiftKey : !event.shiftKey;

  // For 'mod' shortcuts, we need either metaKey (Mac) or ctrlKey (Windows)
  if (hasMod) {
    return (event.metaKey || event.ctrlKey) && altMatch && shiftMatch;
  }

  return modMatch && ctrlMatch && altMatch && shiftMatch;
};
